/**
 * HR Service API
 * Employee records, certifications, leave, and payroll endpoints
 */

import { createAPIClient } from '../client'
import { API_CONFIG } from '../config'

const client = createAPIClient(API_CONFIG.HR.baseURL, API_CONFIG.HR.timeout)

export const hrService = {
  // ==================== Employees ====================

  /**
   * Get all employees with optional filters
   * @param {object} params - Query parameters
   * @returns {Promise} Employee data with pagination
   */
  getEmployees: async (params = {}) => {
    const response = await client.get('/employees/', { params })
    return response.data
  },

  /**
   * Get employee by ID
   * @param {number} id - Employee ID
   * @returns {Promise} Employee data
   */
  getEmployee: async (id) => {
    const response = await client.get(`/employees/${id}`)
    return response.data
  },

  /**
   * Get employee by employee number
   * @param {string} employeeNumber - Employee number
   * @returns {Promise} Employee data
   */
  getEmployeeByNumber: async (employeeNumber) => {
    const response = await client.get(`/employees/number/${employeeNumber}`)
    return response.data
  },

  /**
   * Create new employee
   * @param {object} employeeData - Employee data
   * @returns {Promise} Created employee
   */
  createEmployee: async (employeeData) => {
    const response = await client.post('/employees/', employeeData)
    return response.data
  },

  /**
   * Update employee
   * @param {number} id - Employee ID
   * @param {object} employeeData - Updated employee data
   * @returns {Promise} Updated employee
   */
  updateEmployee: async (id, employeeData) => {
    const response = await client.put(`/employees/${id}`, employeeData)
    return response.data
  },

  /**
   * Delete employee (soft delete)
   * @param {number} id - Employee ID
   * @returns {Promise}
   */
  deleteEmployee: async (id) => {
    const response = await client.delete(`/employees/${id}`)
    return response.data
  },

  // ==================== Field PIN ====================
  // The credential a field hand uses at a QR sticker. Issued here, verified by the forms module.
  // The PIN itself only ever travels OUT of the browser: there is no endpoint that returns a PIN
  // somebody already holds, and nothing here should ever be written to make it look like there is.

  /**
   * Get field-PIN status for an employee (never the PIN itself)
   * @param {string} id - Employee ID
   * @returns {Promise} { has_pin, badge_number, set_at, set_by_user_id, last_used_at,
   *                      failed_attempts, is_locked, locked_until }
   */
  getFieldPinStatus: async (id) => {
    const response = await client.get(`/employees/${id}/field-pin`)
    return response.data
  },

  /**
   * Set or reset an employee's field PIN. Clears any lockout and records who issued it.
   * @param {string} id - Employee ID
   * @param {string} pin - The PIN, 6-8 digits
   * @returns {Promise} Updated field-PIN status
   */
  setFieldPin: async (id, pin) => {
    const response = await client.put(`/employees/${id}/field-pin`, { pin })
    return response.data
  },

  /**
   * Revoke an employee's field PIN. Effective immediately, including for an open sticker session.
   * @param {string} id - Employee ID
   * @returns {Promise} Updated field-PIN status
   */
  revokeFieldPin: async (id) => {
    const response = await client.delete(`/employees/${id}/field-pin`)
    return response.data
  },

  /**
   * Ask the server for a random PIN that its own validator will accept.
   * Belongs to nobody until it is PUT — see field_pins.py for why this endpoint exists.
   * @returns {Promise<string>} The suggested PIN
   */
  suggestFieldPin: async () => {
    const response = await client.post('/employees/field-pins/suggest')
    return response.data.pin
  },

  /**
   * Reset field-PIN failure counters. Omit the ids to clear everyone in the caller's scope.
   * The returned `cleared` counts every badge examined, not every badge that needed clearing —
   * don't render it as "N lockouts cleared".
   * @param {string[]} [employeeIds] - Employees to clear
   * @returns {Promise} { cleared }
   */
  clearFieldPinLockouts: async (employeeIds) => {
    const response = await client.post('/employees/field-pins/clear-lockouts',
      employeeIds ? { employee_ids: employeeIds } : {})
    return response.data
  },

  /**
   * Search employees
   * @param {string} searchTerm - Search term
   * @param {object} filters - Additional filters
   * @returns {Promise} Matching employees
   */
  searchEmployees: async (searchTerm, filters = {}) => {
    const response = await client.get('/employees/', {
      params: { search: searchTerm, ...filters },
    })
    return response.data
  },

  /**
   * Typeahead lookup for a picker — GET /employees/picker.
   *
   * NOT `getEmployees`/`searchEmployees` above. Those hit the DIRECTORY endpoint, which has no
   * default row limit on purpose and returns the full employee record per row (phone numbers, home
   * address, emergency contacts). Filling a dropdown from it downloads the entire roster, fully
   * expanded, to render a list of names — and department-scopes the result for every non-admin, so a
   * foreman sees almost nobody and gets no error saying why.
   *
   * The picker endpoint answers the other question: five fields, a hard server-side cap, a required
   * search term, and no department scoping. See hr/backend/app/api/employees.py:pick_employees.
   *
   * It carries NO badge_number, and does not match on one. A badge is the identifier half of the
   * unauthenticated QR-sticker credential (badge + PIN), so it does not travel on a dropdown —
   * department name is what tells two people with the same name apart.
   *
   * 403 is expected for anyone who cannot assemble a crew (the cross-department reach is gated on
   * hr:manage, or hr:view_all together with tk:manage_crews / tk:approve). Callers must SAY so
   * rather than render an empty list; see useEmployeeSearch.
   *
   * @param {string} search - name fragment (required; blank returns [])
   * @param {number} limit - page size; the server clamps it regardless of what is sent
   * @returns {Promise<Array>} [{ id, first_name, last_name, job_title, department }]
   */
  pickEmployees: async (search, limit = 20) => {
    const response = await client.get('/employees/picker', { params: { search, limit } })
    return Array.isArray(response.data) ? response.data : []
  },

  // ==================== Certifications ====================

  /**
   * Get certifications expiring soon
   * @param {number} days - Days until expiration
   * @returns {Promise} Employees with expiring certifications
   */
  getExpiringCertifications: async (days = 30) => {
    // Note: This endpoint may need to be implemented on backend
    // For now, fetch all employees and filter client-side
    const response = await client.get('/employees/')
    const employees = response.data.employees || []
    
    return employees.filter(emp => {
      if (!emp.certifications || !Array.isArray(emp.certifications)) return false
      return emp.certifications.some(cert => {
        if (!cert.expiration_date) return false
        const expDate = new Date(cert.expiration_date)
        const today = new Date()
        const daysUntilExp = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24))
        return daysUntilExp > 0 && daysUntilExp <= days
      })
    })
  },

  // ==================== Leave Management ====================

  /**
   * Get employees currently on leave
   * @returns {Promise} Employees on leave
   */
  getEmployeesOnLeave: async () => {
    const response = await client.get('/employees/', { 
      params: { status: 'on_leave' } 
    })
    return response.data
  },

  /**
   * Get employee leave history
   * @param {number} id - Employee ID
   * @returns {Promise} Leave history
   */
  getEmployeeLeaveHistory: async (id) => {
    // Note: This endpoint may need to be implemented on backend
    const employee = await client.get(`/employees/${id}`)
    return employee.data.leave_history || []
  },

  // ==================== Payroll ====================

  /**
   * Get employees by pay type
   * @param {string} payType - Pay type (hourly, salary, etc.)
   * @returns {Promise} Employees with specified pay type
   */
  getEmployeesByPayType: async (payType) => {
    const response = await client.get('/employees/')
    const allEmployees = response.data
    
    return {
      ...allEmployees,
      employees: (allEmployees.employees || []).filter(emp => emp.pay_type === payType)
    }
  },

  /**
   * Calculate total payroll
   * @param {array} employees - Array of employees
   * @returns {number} Total payroll amount
   */
  calculateTotalPayroll: (employees) => {
    return employees.reduce((total, emp) => {
      return total + (parseFloat(emp.pay_rate) || 0)
    }, 0)
  },

  // ==================== Time Off Management ====================

  /**
   * Create time off request
   * @param {object} requestData - Time off request data
   * @returns {Promise} Created request
   */
  createTimeOffRequest: async (requestData) => {
    const response = await client.post('/time-off/requests', requestData)
    return response.data
  },

  /**
   * Get time off requests with filters
   * @param {object} params - Query parameters (employee_id, status, leave_type, start_date)
   * @returns {Promise} List of requests
   */
  getTimeOffRequests: async (params = {}) => {
    const response = await client.get('/time-off/requests', { params })
    return response.data
  },

  /**
   * Get specific time off request
   * @param {string} requestId - Request ID
   * @returns {Promise} Request data
   */
  getTimeOffRequest: async (requestId) => {
    const response = await client.get(`/time-off/requests/${requestId}`)
    return response.data
  },

  /**
   * Review time off request (approve/deny)
   * @param {string} requestId - Request ID
   * @param {object} reviewData - Review data (status, reviewer_notes)
   * @returns {Promise} Updated request
   */
  reviewTimeOffRequest: async (requestId, reviewData) => {
    const response = await client.put(`/time-off/requests/${requestId}/review`, reviewData)
    return response.data
  },

  /**
   * Cancel time off request
   * @param {string} requestId - Request ID
   * @returns {Promise} Success message
   */
  cancelTimeOffRequest: async (requestId) => {
    const response = await client.delete(`/time-off/requests/${requestId}`)
    return response.data
  },

  /**
   * Get employee's time off balance
   * @param {string} employeeId - Employee ID
   * @returns {Promise} Balance data
   */
  getTimeOffBalance: async (employeeId) => {
    const response = await client.get(`/time-off/balances/${employeeId}`)
    return response.data
  },

  /**
   * Update employee's time off balance
   * @param {string} employeeId - Employee ID
   * @param {object} balanceData - Balance update data
   * @returns {Promise} Updated balance
   */
  updateTimeOffBalance: async (employeeId, balanceData) => {
    const response = await client.put(`/time-off/balances/${employeeId}`, balanceData)
    return response.data
  },

  /**
   * Get comprehensive time off summary for employee
   * @param {string} employeeId - Employee ID
   * @returns {Promise} Complete time off summary
   */
  getTimeOffSummary: async (employeeId) => {
    const response = await client.get(`/time-off/summary/${employeeId}`)
    return response.data
  },

  // ==================== Certifications ====================

  /**
   * Get certifications with optional filters
   * @param {object} params - Query parameters
   * @returns {Promise} Certifications list
   */
  getCertifications: async (params = {}) => {
    const response = await client.get('/certifications/', { params })
    return response.data
  },

  /**
   * Get certification by ID
   * @param {string} certId - Certification ID
   * @returns {Promise} Certification data
   */
  getCertification: async (certId) => {
    const response = await client.get(`/certifications/${certId}`)
    return response.data
  },

  /**
   * Create new certification
   * @param {object} certData - Certification data
   * @returns {Promise} Created certification
   */
  createCertification: async (certData) => {
    const response = await client.post('/certifications/', certData)
    return response.data
  },

  /**
   * Update certification
   * @param {string} certId - Certification ID
   * @param {object} certData - Certification update data
   * @returns {Promise} Updated certification
   */
  updateCertification: async (certId, certData) => {
    const response = await client.put(`/certifications/${certId}`, certData)
    return response.data
  },

  /**
   * Delete certification
   * @param {string} certId - Certification ID
   * @returns {Promise} Success message
   */
  deleteCertification: async (certId) => {
    const response = await client.delete(`/certifications/${certId}`)
    return response.data
  },

  /**
   * Get training records with optional filters
   * @param {object} params - Query parameters
   * @returns {Promise} Training records list
   */
  getTrainingRecords: async (params = {}) => {
    const response = await client.get('/certifications/training', { params })
    return response.data
  },

  /**
   * Create new training record
   * @param {object} trainingData - Training record data
   * @returns {Promise} Created training record
   */
  createTrainingRecord: async (trainingData) => {
    const response = await client.post('/certifications/training', trainingData)
    return response.data
  },

  /**
   * Get certification statistics
   * @returns {Promise} Certification statistics
   */
  getCertificationStatistics: async () => {
    const response = await client.get('/certifications/statistics')
    return response.data
  },

  // ==================== Performance Reviews ====================

  /**
   * Get employees due for performance review
   * @returns {Promise} Employees needing review
   */
  getEmployeesDueForReview: async () => {
    // Note: This endpoint may need to be implemented on backend
    // Placeholder implementation
    const response = await client.get('/employees/')
    return response.data
  },

  // ==================== Statistics ====================

  /**
   * Get HR statistics
   * @returns {Promise} HR statistics
   */
  getStatistics: async () => {
    const response = await client.get('/employees/')
    const employees = response.data.employees || []
    
    return {
      total_employees: employees.length,
      // The API field is employment_status (not status), serialized lowercase ('active'/'on_leave'/
      // 'terminated'/'inactive'). Compare case-insensitively so the dashboard counts correctly
      // regardless of enum casing (was reading e.status -> undefined -> 0 active).
      active_employees: employees.filter(e => String(e.employment_status ?? '').toLowerCase() === 'active').length,
      on_leave: employees.filter(e => String(e.employment_status ?? '').toLowerCase() === 'on_leave').length,
      terminated: employees.filter(e => String(e.employment_status ?? '').toLowerCase() === 'terminated').length,
      by_department: employees.reduce((acc, emp) => {
        const dept = emp.department || 'Unknown'
        acc[dept] = (acc[dept] || 0) + 1
        return acc
      }, {}),
      by_pay_type: employees.reduce((acc, emp) => {
        const type = emp.pay_type || 'Unknown'
        acc[type] = (acc[type] || 0) + 1
        return acc
      }, {})
    }
  },

  // The /assignments/* and /overrides/* client methods lived here and are GONE. HR's main.py
  // mounts no assignments or overrides router — there is no api/assignments.py and no
  // api/overrides.py — so every one of them 404'd, and their only callers were two pages that
  // no file imported and no TABS array listed. Deleted with those pages rather than left as a
  // twelve-method advertisement for a feature that does not exist. If it is wanted later it is
  // a build, not a re-wire, and git history holds all of it.

  // ==================== HR Documents ====================

  /**
   * Get all documents for an employee
   * @param {string} employeeId - Employee ID
   * @param {object} params - Query parameters (document_type, status)
   * @returns {Promise} List of documents
   */
  getDocuments: async (employeeId, params = {}) => {
    const response = await client.get(`/employees/${employeeId}/documents`, { params })
    return response.data
  },

  /**
   * Get document by ID
   * @param {string} documentId - Document ID
   * @returns {Promise} Document data
   */
  getDocument: async (documentId) => {
    const response = await client.get(`/documents/${documentId}`)
    return response.data
  },

  /**
   * Upload new document
   * @param {string} employeeId - Employee ID
   * @param {FormData} formData - Document form data with file
   * @returns {Promise} Created document
   */
  uploadDocument: async (employeeId, formData) => {
    const response = await client.post(`/employees/${employeeId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },

  /**
   * Update document metadata
   * @param {string} documentId - Document ID
   * @param {object} documentData - Updated document data
   * @returns {Promise} Updated document
   */
  updateDocument: async (documentId, documentData) => {
    const response = await client.put(`/documents/${documentId}`, documentData)
    return response.data
  },

  /**
   * Delete document
   * @param {string} documentId - Document ID
   * @returns {Promise} Success message
   */
  deleteDocument: async (documentId) => {
    const response = await client.delete(`/documents/${documentId}`)
    return response.data
  },

  /**
   * Download document file
   * @param {string} documentId - Document ID
   * @returns {Promise} File blob
   */
  downloadDocument: async (documentId) => {
    const response = await client.get(`/documents/${documentId}/download`, {
      responseType: 'blob'
    })
    return response.data
  },

  /**
   * Get expiring documents across all employees
   * @param {number} daysAhead - Days to look ahead (default 30)
   * @returns {Promise} List of expiring documents
   */
  getExpiringDocuments: async (daysAhead = 30) => {
    const response = await client.get('/documents/expiring', { 
      params: { days_ahead: daysAhead } 
    })
    return response.data
  },

  // ========== HR SETTINGS ==========
  getShifts: async (params = {}) => {
    const response = await client.get('/shifts', { params })
    return response.data
  },

  getDepartments: async (params = {}) => {
    const response = await client.get('/settings/departments', { params })
    return response.data
  },

  createDepartment: async (data) => {
    const response = await client.post('/settings/departments', data)
    return response.data
  },

  updateDepartment: async (deptId, data) => {
    const response = await client.patch(`/settings/departments/${deptId}`, data)
    return response.data
  },

  getJobTitles: async (params = {}) => {
    const response = await client.get('/settings/job-titles', { params })
    return response.data
  },

  createJobTitle: async (data) => {
    const response = await client.post('/settings/job-titles', data)
    return response.data
  },

  updateJobTitle: async (titleId, data) => {
    const response = await client.patch(`/settings/job-titles/${titleId}`, data)
    return response.data
  },

  // ==================== Pay Rules (step 3) ====================
  //
  // The first writer `hr_pay_rules` and `hr_holidays` have ever had. Every setting is nullable and
  // ships BLANK: `None` means NOT CONFIGURED and `1.0` means somebody typed one, and they are
  // different answers. Never send 0 for an empty box.

  getPayRules: async ({ projectId = null, employeeId = null } = {}) => {
    const params = {}
    if (projectId) params.project_id = projectId
    if (employeeId) params.employee_id = employeeId
    const response = await client.get('/settings/pay-rules', { params })
    return response.data
  },

  savePayRuleVersion: async (data) => {
    const response = await client.post('/settings/pay-rules', data)
    return response.data
  },

  // Put a crew, or everyone at a site, onto a schedule -- as one row PER PERSON. A crew is how you
  // ASSIGN a schedule, never how it is stored: moving a man between crews deletes the record he was
  // ever on the first, so a crew-keyed rule could not reprint last March.
  adoptSchedule: async (data) => {
    const response = await client.post('/settings/pay-rules/adopt', data)
    return response.data
  },

  getHolidays: async (dateFrom, dateTo) => {
    const response = await client.get('/settings/holidays', {
      params: { date_from: dateFrom, date_to: dateTo },
    })
    return response.data
  },

  saveHoliday: async (data) => {
    const response = await client.post('/settings/holidays', data)
    return response.data
  },

  // The eleven US federal holidays for a year, as a MENU. Read-only - it writes nothing, so opening
  // the calendar can never overwrite an answer somebody has already given.
  getHolidayProposals: async (year) => {
    const response = await client.get('/settings/holidays/proposals', { params: { year } })
    return response.data
  },

  // ==================== Rate Library ====================

  getLaborRates: async () => {
    const response = await client.get('/settings/labor-rates')
    return response.data
  },

  getLaborClassifications: async () => {
    const response = await client.get('/settings/labor-rates/classifications')
    return response.data
  },

  saveLaborRate: async (data) => {
    const response = await client.post('/settings/labor-rates', data)
    return response.data
  },

  deleteLaborRate: async (id) => {
    const response = await client.delete(`/settings/labor-rates/${id}`)
    return response.data
  },

  getEquipment: async () => {
    const response = await client.get('/settings/equipment')
    return response.data
  },

  getEquipmentNames: async () => {
    const response = await client.get('/settings/equipment/names')
    return response.data
  },

  saveEquipment: async (data) => {
    const response = await client.post('/settings/equipment', data)
    return response.data
  },

  deleteEquipment: async (id) => {
    const response = await client.delete(`/settings/equipment/${id}`)
    return response.data
  },

  getRateHistory: async (rateType = 'labor', itemId = null) => {
    const params = { rate_type: rateType }
    if (itemId != null) params.item_id = itemId
    const response = await client.get('/settings/rate-history', { params })
    return response.data
  },

  // ==================== Pay Grades ====================

  getPayScales: async (params = {}) => {
    const response = await client.get('/settings/pay-grades', { params })
    return response.data
  },

  getPayGrades: async (params = {}) => {
    const response = await client.get('/settings/pay-grades', { params })
    return response.data
  },

  createPayScale: async (data) => {
    const response = await client.post('/settings/pay-grades', data)
    return response.data
  },

  updatePayScale: async (gradeId, data) => {
    const response = await client.patch(`/settings/pay-grades/${gradeId}`, data)
    return response.data
  },

  getPayGradesPdf: async () => {
    const response = await client.get('/settings/pay-grades/pdf', { responseType: 'blob' })
    return response.data
  },

  // ==================== Overtime Rules ====================

  getOvertimeRules: async (params = {}) => {
    const response = await client.get('/compensation/overtime-rules', { params })
    return response.data
  },

  createOvertimeRule: async (data) => {
    const response = await client.post('/compensation/overtime-rules', data)
    return response.data
  },

  // ==================== Compensation Statistics ====================

  getCompensationStatistics: async () => {
    const response = await client.get('/compensation/statistics')
    return response.data
  },

  // ==================== Excel Import ====================

  downloadImportTemplate: async (importType) => {
    const response = await client.get(`/settings/import/template/${importType}`, { responseType: 'blob' })
    return response.data
  },

  importExcel: async (importType, file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await client.post(`/settings/import/${importType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },
}

// Exported so a test can install a fake axios adapter at the TRANSPORT layer and still exercise the
// real request interceptor — the same shape the module contract tests use with each module's own
// client. Stubbing `hrService` instead would test around the layer where a request's params get lost.
export const hrClient = client
